import React, { useState } from "react";
import { Warning, CaretDown, ArrowSquareOut } from "@phosphor-icons/react";
import { relTime } from "../lib/incidents";
import { useAutoExpandNew } from "../lib/useAutoExpandNew";

/**
 * EvacAlertsCard — surfaces active NWS Evacuation / Civil-Emergency / Shelter-in-Place
 * alerts at the top of the sidebar so users see them even without zooming the map.
 * Renders nothing when there are no active alerts.
 */
export default function EvacAlertsCard({ evacuationZones }) {
  const [open, setOpen] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const features = evacuationZones?.features || [];
  useAutoExpandNew(
    features.map((f) => f.properties?.id || f.id).filter(Boolean),
    "okwatch:evac:nws:seen",
    setOpen
  );
  if (features.length === 0) return null;

  return (
    <div data-testid="evac-alerts-card" className="border-b border-tac-warning/40 bg-tac-warning/8">
      <div className="px-4 py-2.5 flex items-center justify-between border-b border-tac-warning/30">
        <button
          data-testid="evac-alerts-header"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-2 group flex-1 min-w-0 text-left"
        >
          <CaretDown
            size={11}
            weight="bold"
            className={`text-tac-warning transition-transform shrink-0 ${open ? "" : "-rotate-90"}`}
          />
          <Warning size={14} weight="fill" className="text-tac-warning shrink-0 animate-pulse" />
          <span className="text-[12px] uppercase tracking-[0.15em] font-bold text-tac-warning">
            Active Evacuation Alerts
          </span>
          <span className="text-[12px] font-mono-tac tabular text-tac-warning/80">· {features.length}</span>
        </button>
      </div>
      {open && (
        <ul className="divide-y divide-tac-warning/20">
          {features.map((f) => {
            const p = f.properties || {};
            const isExpanded = expandedId === p.id;
            return (
              <li
                key={p.id || f.id || JSON.stringify(p).slice(0, 20)}
                data-testid={`evac-alert-${p.id || ""}`}
                className="hover:bg-tac-warning/5 transition-colors"
              >
                <button
                  onClick={() => setExpandedId(isExpanded ? null : p.id)}
                  className="w-full text-left px-4 py-2.5 flex items-start gap-2"
                >
                  <Warning size={12} weight="fill" className="text-tac-warning shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                      <span className="text-[11px] font-bold uppercase tracking-[0.12em] px-1.5 py-0.5 rounded-sm border text-tac-warning border-tac-warning/55 bg-tac-warning/15">
                        {p.severity || "ALERT"}
                      </span>
                      <span className="text-[12px] font-mono-tac uppercase tracking-wider text-tac-warning/80 truncate">
                        {p.event}
                      </span>
                    </div>
                    <div className={`text-xs text-tac-text leading-snug ${isExpanded ? "" : "line-clamp-2"}`}>
                      {p.headline || p.event}
                    </div>
                    {isExpanded && p.area_desc && (
                      <div className="text-[14px] text-tac-dim mt-1.5 leading-relaxed">
                        {p.area_desc}
                      </div>
                    )}
                    {isExpanded && p.expires && (
                      <div className="text-[15px] font-mono-tac text-tac-faint mt-1">
                        Expires {new Date(p.expires).toLocaleString()}
                      </div>
                    )}
                  </div>
                  <CaretDown
                    size={10}
                    weight="bold"
                    className={`text-tac-warning/60 transition-transform shrink-0 mt-1 ${isExpanded ? "rotate-180" : ""}`}
                  />
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {open && (
        <div className="px-4 py-2 text-[11px] uppercase tracking-wider text-tac-warning/70 font-mono-tac border-t border-tac-warning/20">
          Source: NWS active alerts · always confirm with local Sheriff / EM
        </div>
      )}
    </div>
  );
}
